import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
} from '@heroui/react';
import React, { useMemo, useState } from 'react';

import { useMemberActions } from '../../hooks';
import type { AssignableLocation } from '../../types';
import { locationDepth, locationLabel, tierStyle } from '../../utils';

export interface AssignRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  /** The member being given a role; empty when the modal is closed. */
  userId: string;
  memberName: string;
  locations: AssignableLocation[];
  onChanged: () => Promise<void>;
}

export const AssignRoleModal: React.FC<AssignRoleModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
  userId,
  memberName,
  locations,
  onChanged,
}) => {
  const { assignRole, isWorking, error, clearError } =
    useMemberActions(onChanged);
  const [locationId, setLocationId] = useState('');
  const [roleId, setRoleId] = useState('');

  // A role only applies at its own tier, so the choice narrows to the picked
  // place rather than offering every role in the workspace.
  const rolesAtLocation = useMemo(
    () => locations.find((l) => l.locationId === locationId)?.roles ?? [],
    [locations, locationId]
  );

  const close = () => {
    clearError();
    setLocationId('');
    setRoleId('');
    onClose();
  };

  const submit = async () => {
    const assigned = await assignRole({
      workspaceId,
      userId,
      locationId,
      roleId,
    });
    if (assigned) {
      close();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={close} size="md">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          Add role
          <span className="text-small font-normal text-default-500">
            {memberName} — a new role replaces any they already hold there.
          </span>
        </ModalHeader>
        <ModalBody className="gap-4">
          <Select
            label="Where"
            placeholder="Pick one"
            variant="bordered"
            isDisabled={isWorking}
            selectedKeys={locationId ? [locationId] : []}
            onSelectionChange={(keys) => {
              setLocationId(String([...keys][0] ?? ''));
              // The old role may not apply at the new place.
              setRoleId('');
            }}
          >
            {locations.map((location) => {
              const tier = tierStyle(location.hierarchyType);
              const depth = locationDepth(location.hierarchyType);
              const name = locationLabel(
                location.locationName,
                location.locationId
              );

              return (
                // textValue keeps the closed control showing just the name,
                // and the indent mirrors the Organization tab so the same
                // tree is recognisable here.
                <SelectItem key={location.locationId} textValue={name}>
                  <div
                    className="flex items-center gap-2"
                    style={{ marginLeft: `${depth * 16}px` }}
                  >
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${tier.dot}`}
                      aria-hidden
                    />
                    <span>{name}</span>
                    <span className="text-tiny text-default-500">
                      {tier.label}
                    </span>
                  </div>
                </SelectItem>
              );
            })}
          </Select>

          <Select
            label="Role"
            placeholder={
              locationId ? 'What they can do there' : 'Pick where first'
            }
            variant="bordered"
            isDisabled={isWorking || !locationId}
            selectedKeys={roleId ? [roleId] : []}
            onSelectionChange={(keys) => setRoleId(String([...keys][0] ?? ''))}
          >
            {rolesAtLocation.map((role) => (
              <SelectItem key={role.id}>{role.displayName}</SelectItem>
            ))}
          </Select>

          {error && (
            <div className="rounded-medium bg-danger-50 p-3 text-small text-danger">
              {error}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={close} isDisabled={isWorking}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={submit}
            isDisabled={!locationId || !roleId || isWorking}
            isLoading={isWorking}
          >
            Add
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
